import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Button } from '@adobe/react-spectrum';
import './EnhancedAlertBanner.scss';

export type EnhancedAlertVariant = 'success' | 'error' | 'info';

interface EnhancedAlertBannerProps {
  variant: EnhancedAlertVariant;
  message: React.ReactNode;
  onClose: () => void;
  autoHide?: boolean;
  autoHideDelay?: number;
}

export const EnhancedAlertBanner: React.FC<EnhancedAlertBannerProps> = ({
  variant,
  message,
  onClose,
  autoHide = false,
  autoHideDelay = 5000
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(true);

  // Animation effect
  useEffect(() => {
    // Trigger entrance animation
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  // Auto-hide effect
  useEffect(() => {
    if (autoHide) {
      const timer = setTimeout(() => {
        handleClose();
      }, autoHideDelay);
      return () => clearTimeout(timer);
    }
  }, [autoHide, autoHideDelay]);

  const handleClose = () => {
    setIsVisible(false);
    // Wait for exit animation to complete before calling onClose
    setTimeout(() => {
      setShouldRender(false);
      onClose();
    }, 300);
  };

  if (!shouldRender) return null;

  return ReactDOM.createPortal(
    <div
      className={`enhanced-alert-banner enhanced-alert-banner--${variant} ${
        isVisible ? 'enhanced-alert-banner--visible' : ''
      } ${autoHide ? 'auto-hide' : ''}`}
      role="alert"
      aria-live="polite"
    >
      <div className="enhanced-alert-banner__content">
        <span className="enhanced-alert-banner__message">{message}</span>
        <Button
          variant="secondary"
          isQuiet
          aria-label="Close notification"
          onPress={handleClose}
          UNSAFE_className="enhanced-alert-banner__close"
        >
          ×
        </Button>
      </div>
      <div className="enhanced-alert-banner__progress-bar" />
    </div>,
    document.body
  );
};
